import React, { useEffect, useRef, useState } from 'react';
import {
  type ControlSnapshot, type WorkPackage, type ResourceCapacity, type PlanningChange, type CapacityDay,
  type PackageStatus, packageStatuses,
} from '../services/projectControlCentreService';

export const statusLabels: Record<PackageStatus, string> = {
  ready: 'Ready', in_progress: 'In progress', waiting: 'Waiting', complete: 'Complete',
};
export type EditorTarget =
  | { kind: 'package'; item?: WorkPackage; projectId?: string; focusHours?: boolean }
  | { kind: 'capacity'; item?: ResourceCapacity; date?: string; basis?: CapacityDay }
  | { kind: 'wip_limit'; resourceId: string }
  | { kind: 'commitment'; projectId: string };

export function HoursField({ label, name, initial, max, presets, disabled, required }: {
  label: string; name: string; initial?: number | null; max: number; presets: number[]; disabled?: boolean; required?: boolean;
}) {
  const [draft, setDraft] = useState(String(initial ?? ''));
  const value = disabled ? '0' : draft;
  return <div className="pcc-hours-field">
    <label>{label}<input name={name} type="number" min="0" step="0.01" max={max} required={required}
      disabled={disabled} value={value} placeholder={required ? undefined : 'Unknown'} onChange={e => setDraft(e.target.value)} /></label>
    <div role="group" aria-label={label + ' presets'} className="pcc-hours-strip">
      {presets.map(n => <button key={n} type="button" disabled={disabled}
        aria-pressed={value !== '' && Number(value) === n} onClick={() => setDraft(String(n))}>{n}h</button>)}
    </div>
  </div>;
}

export default function PlanningEditor({ snapshot, target, busy, onSave, onClose }: {
  snapshot: ControlSnapshot; target: EditorTarget; busy: boolean;
  onSave: (change: PlanningChange) => void; onClose: () => void;
}) {
  const item = target.kind === 'package' ? target.item : undefined;
  const capacity = target.kind === 'capacity' ? target.item : undefined;
  const basis = target.kind === 'capacity' ? target.basis : undefined;
  const limit = target.kind === 'wip_limit' ? snapshot.wipLimits?.find(row => row.resourceId === target.resourceId) : undefined;
  const [projectId, setProjectId] = useState(
    target.kind === 'commitment' ? target.projectId : item?.projectId || (target.kind === 'package' && target.projectId) || snapshot.projects[0]?.projectId || ''
  );
  const [status, setStatus] = useState<PackageStatus>(item?.status || 'ready');
  const project = snapshot.projects.find(p => p.projectId === projectId);
  const focus = useRef<HTMLHeadingElement>(null);
  const [packageId] = useState(() => item?.id || (target.kind === 'package' ? crypto.randomUUID() : ''));
  useEffect(() => {
    if (target.kind === 'package' && target.focusHours) {
      focus.current?.parentElement?.querySelector<HTMLInputElement>('input[name="remaining"]')?.focus();
    } else focus.current?.focus();
  }, [target]);
  const names = Array.from(new Set([
    ...snapshot.capacity.map(c => c.resourceId),
    ...snapshot.plan.days.map(d => d.resourceId),
    ...snapshot.packages.flatMap(p => p.resourceId ? [p.resourceId] : []),
  ])).sort();
  const heading = target.kind === 'package' ? (item ? 'Edit work package' : 'Add work package')
    : target.kind === 'capacity' ? 'Record daily capacity'
    : target.kind === 'wip_limit' ? 'Edit personal WIP limits' : 'Update delivery commitment';

  function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const field = (key: string) => String(form.get(key) || '').trim();
    if (target.kind === 'package') {
      onSave({ kind: 'package', id: packageId, version: item?.version ?? 0, projectId, status,
        title: field('title'), resourceId: field('resource') || null, priority: Number(field('priority')),
        remainingHours: status === 'complete' ? 0 : field('remaining') ? Number(field('remaining')) : null,
        dependencies: form.getAll('dependencies').map(String) });
    } else if (target.kind === 'capacity') {
      const resourceId = capacity?.resourceId ?? basis?.resourceId ?? field('resource').toLowerCase();
      const date = field('date');
      onSave({ kind: 'capacity', resourceId, date, version: capacity?.version ?? 0,
        hours: Number(field('hours')), confirmed: form.has('confirmed') });
    } else if (target.kind === 'wip_limit') {
      onSave({ kind: 'wip_limit', resourceId: target.resourceId, version: limit?.version ?? 0,
        maxProjects: Number(field('maxProjects')), maxHours: Number(field('maxHours')) });
    } else if (project) {
      onSave({ kind: 'commitment', projectId, version: project.version, committedDate: field('date') || null });
    }
  }

  return <section className="pcc-panel pcc-editor" aria-labelledby="pcc-editor-heading">
    <h2 id="pcc-editor-heading" tabIndex={-1} ref={focus}>{heading}</h2>
    <form onSubmit={submit}>
      <fieldset disabled={busy}>
        <div className="pcc-fields">
          {target.kind === 'package' && <>
            <label>Project<select value={projectId} onChange={event => setProjectId(event.target.value)} disabled={!!item} required>
              {snapshot.projects.map(p => <option key={p.projectId} value={p.projectId}>{p.name}</option>)}
            </select></label>
            <label>Work package<input name="title" required maxLength={200} defaultValue={item?.title} /></label>
            <label>Assigned resource<input name="resource" maxLength={100} list="pcc-resources" defaultValue={item?.resourceId || ''} placeholder="Unassigned" /></label>
            <HoursField label="Remaining hours" name="remaining" initial={item?.remainingHours} max={10000}
              presets={[0, 1, 2, 4, 8]} disabled={status === 'complete'} />
            <label>Status<select value={status} onChange={e => setStatus(e.target.value as PackageStatus)}>
              {packageStatuses.map(s => <option key={s} value={s} disabled={s === 'in_progress' && item?.status !== s &&
                (snapshot.wip === undefined || snapshot.wipLimitsAvailable === false)}>{statusLabels[s]}</option>)}
            </select></label>
            <label>Priority<select name="priority" defaultValue={item?.priority ?? 3}>
              {[1, 2, 3, 4, 5].map(n => <option key={n} value={n}>{n}{n === 1 ? ' · Highest' : n === 5 ? ' · Lowest' : ''}</option>)}
            </select></label>
          </>}
          {target.kind === 'capacity' && <>
            <label>Resource<input name="resource" maxLength={100} required list="pcc-resources" readOnly={!!capacity || !!basis} defaultValue={capacity?.resourceId ?? basis?.resourceId} /></label>
            <label>Work date<input name="date" type="date" required min={snapshot.today} max={snapshot.through} readOnly={!!capacity || !!basis} defaultValue={capacity?.date || basis?.date || target.date || snapshot.today} /></label>
            <HoursField label="Productive hours" name="hours" initial={capacity?.hours ?? basis?.availableHours} max={24} presets={[0, 2, 4, 6, 8]} required />
          </>}
          {target.kind === 'commitment' && <label>{project?.name} · Committed delivery<input name="date" type="date" defaultValue={project?.committedDate || ''} /></label>}
          {target.kind === 'wip_limit' && <>
            <label>Maximum active projects<input name="maxProjects" type="number" required min="0" max="1000" step="1" defaultValue={limit?.maxProjects} /></label>
            <HoursField label="Maximum personal remaining hours" name="maxHours" initial={limit?.maxHours} max={10000} presets={[0, 8, 16, 25, 40]} required />
          </>}
        </div>
        {target.kind === 'package' && <fieldset className="pcc-deps" key={projectId}><legend>Depends on</legend>
          {snapshot.packages.filter(p => p.projectId === projectId && p.id !== item?.id).map(p =>
            <label key={p.id}><input type="checkbox" name="dependencies" value={p.id} defaultChecked={item?.dependencies.includes(p.id)} /> {p.title}</label>)}
          {!snapshot.packages.some(p => p.projectId === projectId && p.id !== item?.id) && <small>No other work packages in this project.</small>}
        </fieldset>}
        {target.kind === 'capacity' && <>
          <p>{basis ? 'This override applies only to the selected date. Enter the hours left for project work and confirm availability.' : 'Enter hours left for project work after appointments, administration and contingency.'}</p>
          <label className="pcc-check"><input name="confirmed" type="checkbox" defaultChecked={capacity?.confirmed ?? false} /> Resource confirmed these hours</label>
        </>}
        {target.kind === 'commitment' && <p>Save the agreed client date. Clear the field to remove the recorded commitment.</p>}
        {target.kind === 'wip_limit' && <p>These limits apply to <strong>{target.resourceId}</strong>. Enter zero to pause new starts.</p>}
        <datalist id="pcc-resources">{names.map(name => <option key={name} value={name} />)}</datalist>
        <div className="pcc-actions"><button className="pcc-primary" type="submit">{busy ? 'Saving…' : 'Save changes'}</button>
          <button type="button" onClick={onClose}>Cancel</button></div>
      </fieldset>
    </form>
  </section>;
}
